/**
 * Live Markdown rendering inside the editor: headings, emphasis, links, quotes, lists, task boxes,
 * tables and maths are shown formatted while you type. The Markdown syntax of an element appears
 * only while the cursor is in it, so it can still be edited. The stored text stays exact Markdown:
 * collaboration, history, export and encryption are unchanged. Figures (`![Caption](bl://file/…)` on a
 * line of their own) show as their image and caption.
 */
import { syntaxTree } from "@codemirror/language";
import { EditorState, Facet, StateEffect, StateField, type Extension, type Range } from "@codemirror/state";
import { Decoration, EditorView, ViewPlugin, WidgetType, type DecorationSet, type ViewUpdate } from "@codemirror/view";
import katex from "katex";
import { findFigures } from "@brainlog/shared";
import { figureImageUrl } from "@/features/files/figureImages";

type SyntaxNode = ReturnType<ReturnType<typeof syntaxTree>["resolveInner"]>;

/** Every replacing decoration made here (to tell them apart from marks and line styles). */
const REPLACING = new WeakSet<Decoration>();
function replace(spec: Parameters<typeof Decoration.replace>[0]) {
  const d = Decoration.replace(spec);
  REPLACING.add(d);
  return d;
}

export type OpenLinkHandler = (href: string, context: string) => void;

// Focus: the syntax of the element under the cursor is shown only while the editor has focus.
const setFocus = StateEffect.define<boolean>();
const focusField = StateField.define<boolean>({
  create: () => false,
  update(value, tr) {
    for (const e of tr.effects) if (e.is(setFocus)) value = e.value;
    return value;
  },
});

function active(state: EditorState, from: number, to: number) {
  if (!state.field(focusField, false)) return false;
  return state.selection.ranges.some((r) => r.from <= to && r.to >= from);
}

/** Links (and citations) show their Markdown only when the cursor is inside them, not just next to them. */
function inside(state: EditorState, from: number, to: number) {
  if (!state.field(focusField, false)) return false;
  return state.selection.ranges.some((r) => (r.empty ? r.from > from && r.from < to : r.from < to && r.to > from));
}

const hide = replace({});
const syntaxMark = Decoration.mark({ class: "cm-md-syntax" });

// Widgets ---------------------------------------------------------------------------------

class BulletWidget extends WidgetType {
  eq() {
    return true;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-md-bullet";
    s.textContent = "•";
    s.setAttribute("aria-hidden", "true");
    return s;
  }
}

class CheckboxWidget extends WidgetType {
  constructor(
    readonly checked: boolean,
    readonly pos: number,
  ) {
    super();
  }
  eq(other: CheckboxWidget) {
    return other.checked === this.checked && other.pos === this.pos;
  }
  toDOM(view: EditorView) {
    const box = document.createElement("input");
    box.type = "checkbox";
    box.className = "cm-md-task";
    box.checked = this.checked;
    box.setAttribute("aria-label", this.checked ? "Done (click to reopen)" : "To do (click to mark done)");
    box.disabled = !view.state.facet(EditorView.editable);
    box.addEventListener("mousedown", (e) => {
      e.preventDefault();
      if (box.disabled) return;
      // The character between the brackets: "[ ]" ↔ "[x]".
      view.dispatch({ changes: { from: this.pos + 1, to: this.pos + 2, insert: this.checked ? " " : "x" } });
    });
    return box;
  }
  ignoreEvent() {
    return true;
  }
}

class LabelWidget extends WidgetType {
  constructor(
    readonly text: string,
    readonly className: string,
  ) {
    super();
  }
  eq(other: LabelWidget) {
    return other.text === this.text && other.className === this.className;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = this.className;
    s.textContent = this.text;
    return s;
  }
}

class RuleWidget extends WidgetType {
  eq() {
    return true;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-md-hr";
    s.setAttribute("role", "separator");
    return s;
  }
}

/** Clicking a rendered block (table, maths) puts the cursor in it, which shows its Markdown. */
function editOnClick(el: HTMLElement, view: EditorView, pos: number) {
  el.addEventListener("mousedown", (e) => {
    if (!view.state.facet(EditorView.editable)) return;
    e.preventDefault();
    view.dispatch({ selection: { anchor: pos } });
    view.focus();
  });
}

class MathWidget extends WidgetType {
  constructor(
    readonly tex: string,
    readonly display: boolean,
    readonly pos: number,
  ) {
    super();
  }
  eq(other: MathWidget) {
    return other.tex === this.tex && other.display === this.display && other.pos === this.pos;
  }
  toDOM(view: EditorView) {
    const el = document.createElement(this.display ? "div" : "span");
    el.className = this.display ? "cm-md-math cm-md-math-display" : "cm-md-math";
    try {
      // KaTeX output (trust: false) contains no scripts or links.
      el.innerHTML = katex.renderToString(this.tex, { displayMode: this.display, throwOnError: false, strict: "ignore", trust: false });
    } catch {
      el.textContent = this.tex;
    }
    editOnClick(el, view, this.pos);
    return el;
  }
  ignoreEvent() {
    return true;
  }
}

/** Cell text with `code`, **bold**, *italic* and $maths$ (built as DOM nodes, never as HTML). */
function inlineCell(text: string, into: HTMLElement) {
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_|\$[^$\n]+\$|\[([^\]]+)\]\([^)]*\))/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > last) into.append(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      const c = document.createElement("code");
      c.textContent = tok.slice(1, -1);
      into.append(c);
    } else if (tok.startsWith("**")) {
      const b = document.createElement("strong");
      b.textContent = tok.slice(2, -2);
      into.append(b);
    } else if (tok.startsWith("$")) {
      const s = document.createElement("span");
      try {
        s.innerHTML = katex.renderToString(tok.slice(1, -1), { throwOnError: false, strict: "ignore", trust: false });
      } catch {
        s.textContent = tok;
      }
      into.append(s);
    } else if (tok.startsWith("[")) {
      into.append(m[2]);
    } else {
      const i = document.createElement("em");
      i.textContent = tok.slice(1, -1);
      into.append(i);
    }
    last = m.index! + tok.length;
  }
  if (last < text.length) into.append(text.slice(last));
}

function splitRow(line: string) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  return s.split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, "|"));
}

class TableWidget extends WidgetType {
  constructor(
    readonly source: string,
    readonly pos: number,
  ) {
    super();
  }
  eq(other: TableWidget) {
    return other.source === this.source && other.pos === this.pos;
  }
  toDOM(view: EditorView) {
    const wrap = document.createElement("div");
    wrap.className = "cm-md-table";
    const table = document.createElement("table");
    const lines = this.source.split("\n").filter((l) => l.trim());
    const align = lines[1] ? splitRow(lines[1]).map((c) => (/^:-+:$/.test(c) ? "center" : /-+:$/.test(c) ? "right" : "left")) : [];
    lines.forEach((line, i) => {
      if (i === 1) return;
      const tr = document.createElement("tr");
      splitRow(line).forEach((cell, j) => {
        const td = document.createElement(i === 0 ? "th" : "td");
        td.style.textAlign = align[j] ?? "left";
        inlineCell(cell, td);
        tr.append(td);
      });
      (i === 0 ? (table.tHead ?? table.createTHead()) : (table.tBodies[0] ?? table.createTBody())).append(tr);
    });
    wrap.append(table);
    editOnClick(wrap, view, this.pos);
    return wrap;
  }
  ignoreEvent() {
    return true;
  }
}

// Figures -------------------------------------------------------------------------------------

/** Where figure numbers start in this text (a manuscript section after others), and its project. */
export interface FigureContext {
  start: number;
  workspaceId?: string;
}
type FigureRef = { current: FigureContext | null | undefined };
const figureFacet = Facet.define<FigureRef, FigureRef | null>({ combine: (v) => v[0] ?? null });
/** Redraw figures (their numbers changed). */
export const refreshFigures = StateEffect.define<null>();

/** Under a figure line: its image and "Figure n. Caption" (the number in manuscripts only). */
class FigureWidget extends WidgetType {
  constructor(
    readonly id: string,
    readonly caption: string,
    readonly number: number | null,
    readonly workspaceId: string | undefined,
  ) {
    super();
  }
  eq(other: FigureWidget) {
    return other.id === this.id && other.caption === this.caption && other.number === this.number && other.workspaceId === this.workspaceId;
  }
  toDOM(view: EditorView) {
    const fig = document.createElement("figure");
    fig.className = "cm-md-figure";
    const frame = document.createElement("div");
    frame.className = "cm-md-figure-image";
    const status = document.createElement("span");
    status.className = "cm-md-figure-status";
    status.textContent = "Loading the image…";
    frame.append(status);
    const caption = document.createElement("figcaption");
    if (this.number !== null) {
      const label = document.createElement("strong");
      label.textContent = `Figure ${this.number}.`;
      caption.append(label, this.caption ? ` ${this.caption}` : "");
    } else caption.textContent = this.caption;
    fig.append(frame, caption);
    figureImageUrl(this.id, this.workspaceId).then(
      (url) => {
        const img = document.createElement("img");
        img.src = url;
        img.alt = this.caption || (this.number !== null ? `Figure ${this.number}` : "Figure");
        frame.replaceChildren(img);
      },
      (err: Error) => {
        status.textContent = err.message;
        status.classList.add("cm-md-figure-error");
      },
    );
    // A click puts the cursor on the figure's line, to change its caption.
    fig.addEventListener("mousedown", (e) => {
      if (!view.state.facet(EditorView.editable)) return;
      e.preventDefault();
      view.dispatch({ selection: { anchor: view.state.doc.lineAt(view.posAtDOM(fig)).from } });
      view.focus();
    });
    return fig;
  }
  ignoreEvent() {
    return true;
  }
}

// Inline rendering (visible part of the document only) ---------------------------------------

const LIST_BULLET = /^[-*+]$/;
const MATH_INLINE = /(?<![\\$])\$(?=\S)((?:\\\$|[^$\n])+?)(?<=\S)\$(?![\d$])/g;

function inCode(state: EditorState, pos: number) {
  for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, 1); n; n = n.parent) {
    if (/Code|Comment|HTML|URL/.test(n.name)) return true;
  }
  return false;
}

export function internalLinkClass(href: string) {
  const type = /^bl:\/\/([a-z]+)\//.exec(href)?.[1];
  return type ? `cm-md-link cm-md-link-internal cm-md-link-${type}` : "cm-md-link";
}

function buildInline(view: EditorView): DecorationSet {
  const { state } = view;
  const tree = syntaxTree(state);
  const out: Range<Decoration>[] = [];
  const doc = state.doc;
  const lineActive = (pos: number) => {
    const line = doc.lineAt(pos);
    return active(state, line.from, line.to);
  };

  for (const { from, to } of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: (n) => {
        const name = n.name;
        const heading = /^ATXHeading(\d)$/.exec(name);
        if (heading) {
          const line = doc.lineAt(n.from);
          out.push(Decoration.line({ class: `cm-md-h cm-md-h${heading[1]}` }).range(line.from));
          const show = active(state, line.from, line.to);
          n.node.getChildren("HeaderMark").forEach((m, i) => {
            if (show) return void out.push(syntaxMark.range(m.from, m.to));
            // "# " at the start, " ##" at the end.
            const start = i === 0 ? m.from : Math.max(line.from, m.from - 1);
            const end = i === 0 ? Math.min(line.to, m.to + 1) : m.to;
            if (end > start) out.push(hide.range(start, end));
          });
          return;
        }
        if (/^SetextHeading(\d)$/.test(name)) {
          const level = name.endsWith("1") ? 1 : 2;
          out.push(Decoration.line({ class: `cm-md-h cm-md-h${level}` }).range(doc.lineAt(n.from).from));
          const mark = n.node.getChild("HeaderMark");
          if (mark) out.push(syntaxMark.range(mark.from, mark.to));
          return;
        }
        if (name === "Emphasis" || name === "StrongEmphasis" || name === "Strikethrough" || name === "InlineCode") {
          const marks = n.node.getChildren(name === "Strikethrough" ? "StrikethroughMark" : name === "InlineCode" ? "CodeMark" : "EmphasisMark");
          const show = active(state, n.from, n.to);
          for (const m of marks) out.push((show ? syntaxMark : hide).range(m.from, m.to));
          if (name === "InlineCode") out.push(Decoration.mark({ class: "cm-md-code" }).range(n.from, n.to));
          return;
        }
        if (name === "Link") {
          const marks = n.node.getChildren("LinkMark");
          const url = n.node.getChild("URL");
          if (marks.length < 2) return;
          const textFrom = marks[0].to;
          const textTo = marks[1].from;
          const href = url ? state.sliceDoc(url.from, url.to) : "";
          if (inside(state, n.from, n.to) || textTo <= textFrom || !href) {
            if (url) out.push(Decoration.mark({ class: "cm-md-url" }).range(url.from, url.to));
            return;
          }
          // A citation reads as "(Author, Year)" when the paper is in the library.
          const label = href.startsWith("bl://cite/") ? citeLabel(state, href, state.sliceDoc(textFrom, textTo)) : null;
          if (label) {
            out.push(replace({ widget: new CiteWidget(label, href) }).range(n.from, n.to));
            return;
          }
          out.push(hide.range(n.from, textFrom));
          out.push(
            Decoration.mark({
              class: internalLinkClass(href),
              attributes: { "data-href": href, title: href.startsWith("bl://") ? "Open beside the note (Alt+click to edit the link)" : `${href} — opens in a new tab (Alt+click to edit)` },
            }).range(textFrom, textTo),
          );
          out.push(hide.range(textTo, n.to));
          return;
        }
        if (name === "URL" && !["Link", "Image", "LinkReference"].includes(n.node.parent?.name ?? "")) {
          const href = state.sliceDoc(n.from, n.to);
          if (/^(https?:\/\/|www\.)/i.test(href) && !active(state, n.from, n.to))
            out.push(Decoration.mark({ class: "cm-md-link", attributes: { "data-href": href.startsWith("www.") ? `https://${href}` : href } }).range(n.from, n.to));
          return;
        }
        if (name === "Blockquote") {
          const first = doc.lineAt(n.from);
          const callout = /^(\s*>\s?)\[!AI-ASSISTED\]\s*/i.exec(first.text);
          for (let pos = n.from; pos <= n.to; ) {
            const line = doc.lineAt(pos);
            out.push(Decoration.line({ class: callout ? "cm-md-quote cm-md-callout" : "cm-md-quote" }).range(line.from));
            pos = line.to + 1;
          }
          if (callout) {
            const start = first.from + callout[1].length;
            const end = first.from + callout[0].length;
            if (active(state, first.from, first.to)) out.push(syntaxMark.range(start, end));
            else out.push(replace({ widget: new LabelWidget("AI-assisted", "cm-md-callout-label") }).range(start, end));
          }
          return;
        }
        if (name === "QuoteMark") {
          if (!lineActive(n.from)) {
            const end = state.sliceDoc(n.to, n.to + 1) === " " ? n.to + 1 : n.to;
            out.push(hide.range(n.from, end));
          } else out.push(syntaxMark.range(n.from, n.to));
          return;
        }
        if (name === "ListMark") {
          const mark = state.sliceDoc(n.from, n.to);
          const task = n.node.parent?.getChild("Task");
          const show = lineActive(n.from);
          if (task && !show) {
            // A task item shows only its checkbox.
            out.push(hide.range(n.from, Math.min(task.from, n.to + 1)));
          } else if (LIST_BULLET.test(mark) && !show) {
            out.push(replace({ widget: new BulletWidget() }).range(n.from, n.to));
          } else out.push(Decoration.mark({ class: "cm-md-listmark" }).range(n.from, n.to));
          return;
        }
        if (name === "TaskMarker") {
          const checked = /x/i.test(state.sliceDoc(n.from, n.to));
          if (!lineActive(n.from)) out.push(replace({ widget: new CheckboxWidget(checked, n.from) }).range(n.from, n.to));
          if (checked) {
            const line = doc.lineAt(n.from);
            if (n.to < line.to) out.push(Decoration.mark({ class: "cm-md-done" }).range(n.to, line.to));
          }
          return;
        }
        if (name === "HorizontalRule") {
          if (!lineActive(n.from)) out.push(replace({ widget: new RuleWidget() }).range(n.from, n.to));
          return;
        }
        if (name === "FencedCode" || name === "CodeBlock") {
          for (let pos = n.from; pos <= n.to; ) {
            const line = doc.lineAt(pos);
            out.push(Decoration.line({ class: "cm-md-codeblock" }).range(line.from));
            pos = line.to + 1;
          }
          n.node.getChildren("CodeMark").forEach((m) => out.push(syntaxMark.range(m.from, m.to)));
          const info = n.node.getChild("CodeInfo");
          if (info) out.push(syntaxMark.range(info.from, info.to));
          return false;
        }
        if (name === "Comment" || name === "CommentBlock") {
          // "<!-- Your own reading -->" reads as a quiet hint.
          const text = state.sliceDoc(n.from, n.to);
          if (text.startsWith("<!--") && text.endsWith("-->") && !active(state, n.from, n.to) && n.to - n.from > 7) {
            out.push(hide.range(n.from, n.from + 4));
            out.push(Decoration.mark({ class: "cm-md-comment" }).range(n.from + 4, n.to - 3));
            out.push(hide.range(n.to - 3, n.to));
          } else out.push(Decoration.mark({ class: "cm-md-comment" }).range(n.from, n.to));
          return false;
        }
        if (name === "Escape" && !active(state, n.from, n.to)) {
          out.push(hide.range(n.from, n.from + 1));
          return;
        }
      },
    });

    // Inline maths: $…$ (not inside code, links or comments).
    for (let pos = from; pos <= to; ) {
      const line = doc.lineAt(pos);
      if (line.text.includes("$") && !/^\s*\$\$\s*$/.test(line.text)) {
        for (const m of line.text.matchAll(MATH_INLINE)) {
          const start = line.from + m.index!;
          const end = start + m[0].length;
          if (inCode(state, start) || active(state, start, end)) continue;
          out.push(replace({ widget: new MathWidget(m[1], false, start) }).range(start, end));
        }
        const single = /^\s*\$\$(.+)\$\$\s*$/.exec(line.text);
        if (single && !active(state, line.from, line.to) && !inCode(state, line.from))
          out.push(replace({ widget: new MathWidget(single[1], true, line.from) }).range(line.from, line.to));
      }
      pos = line.to + 1;
    }
  }
  return Decoration.set(dedupe(out), true);
}

/** Replacing ranges must not overlap: of two overlapping ones, the one that starts first is kept. */
function dedupe(ranges: Range<Decoration>[]) {
  const replacing = (r: Range<Decoration>) => r.to > r.from && REPLACING.has(r.value);
  const reps = ranges.filter(replacing).sort((a, b) => a.from - b.from || b.to - a.to);
  const kept: Range<Decoration>[] = [];
  let until = -1;
  for (const r of reps) {
    if (r.from < until) continue;
    kept.push(r);
    until = r.to;
  }
  return [...ranges.filter((r) => !replacing(r)), ...kept];
}



const inlinePlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = buildInline(view);
    }
    update(u: ViewUpdate) {
      if (
        u.docChanged ||
        u.viewportChanged ||
        u.selectionSet ||
        u.focusChanged ||
        syntaxTree(u.startState) !== syntaxTree(u.state) ||
        u.transactions.some((t) => t.effects.some((e) => e.is(setFocus) || e.is(refreshCitations)))
      )
        this.decorations = buildInline(u.view);
    }
  },
  { decorations: (p) => p.decorations },
);

// Block rendering (tables, display maths): whole document, as block widgets ---------------------

function buildBlocks(state: EditorState): DecorationSet {
  const out: Range<Decoration>[] = [];
  const doc = state.doc;
  syntaxTree(state).iterate({
    enter: (n) => {
      if (n.name === "Table") {
        const from = doc.lineAt(n.from).from;
        const to = doc.lineAt(n.to).to;
        if (!active(state, from, to)) out.push(replace({ widget: new TableWidget(state.sliceDoc(from, to), from), block: true }).range(from, to));
        return false;
      }
      if (n.name === "FencedCode" || n.name === "CodeBlock" || n.name === "CommentBlock" || n.name === "HTMLBlock") return false;
    },
  });
  // $$ on its own line … $$ on its own line.
  for (let i = 1; i <= doc.lines; i++) {
    const open = doc.line(i);
    if (!/^\s*\$\$\s*$/.test(open.text) || inCode(state, open.from)) continue;
    let j = i + 1;
    while (j <= doc.lines && !/^\s*\$\$\s*$/.test(doc.line(j).text)) j++;
    if (j > doc.lines) break;
    const close = doc.line(j);
    const tex = doc.sliceString(open.to + 1, close.from - 1);
    if (!active(state, open.from, close.to) && tex.trim()) out.push(replace({ widget: new MathWidget(tex, true, open.from), block: true }).range(open.from, close.to));
    i = j;
  }
  // Figures: the line `![Caption](bl://file/…)` shows as its image and caption; while the cursor is on
  // it, the line itself shows too (above the image), so the caption can be changed.
  const figures = state.facet(figureFacet)?.current;
  findFigures(doc.toString()).forEach((f, i) => {
    const widget = new FigureWidget(f.id, f.caption, figures ? figures.start + i : null, figures?.workspaceId);
    const line = doc.line(f.line);
    if (active(state, line.from, line.to)) out.push(Decoration.widget({ widget, block: true, side: 1 }).range(line.to));
    else out.push(Decoration.replace({ widget, block: true }).range(line.from, line.to));
  });
  return Decoration.set(out, true);
}

const blockField = StateField.define<DecorationSet>({
  create: (state) => buildBlocks(state),
  update(value, tr) {
    if (tr.docChanged || tr.selection || syntaxTree(tr.startState) !== syntaxTree(tr.state) || tr.effects.some((e) => e.is(setFocus) || e.is(refreshFigures))) return buildBlocks(tr.state);
    return value;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// Links -------------------------------------------------------------------------------------

/**
 * The text around a link (its list item, paragraph, table row or whole quote), as plain text:
 * the side panel uses it to show the quotation the link supports.
 */
export function linkContext(state: EditorState, pos: number) {
  let block: SyntaxNode | null = null;
  for (let n: SyntaxNode | null = syntaxTree(state).resolveInner(pos, 1); n; n = n.parent) {
    if (n.name === "Blockquote") {
      block = n;
      break;
    }
    if (!block && /^(Paragraph|TableRow|ListItem|ATXHeading\d)$/.test(n.name)) block = n;
  }
  const raw = block ? state.sliceDoc(block.from, block.to) : state.doc.lineAt(pos).text;
  return raw
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+(\[[ xX]\]\s+)?/gm, "")
    .replace(/\*\*|__/g, "")
    .trim();
}

/** How a citation reads in the text: "(Smith et al., 2024)", from the library when known. */
type CiteLabels = { current: { items(): { id: string; authors: string; year: number | null }[] } | null | undefined };
const citeLabelsFacet = Facet.define<CiteLabels, CiteLabels | null>({ combine: (v) => v[0] ?? null });
/** Redraw citations (the library finished loading or changed). */
export const refreshCitations = StateEffect.define<null>();

class CiteWidget extends WidgetType {
  constructor(
    readonly label: string,
    readonly href: string,
  ) {
    super();
  }
  eq(other: CiteWidget) {
    return other.label === this.label && other.href === this.href;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-md-link cm-md-link-internal cm-md-link-cite";
    s.setAttribute("data-href", this.href);
    s.title = "Open the cited paper beside the note (Alt+click to edit)";
    s.textContent = this.label;
    return s;
  }
  ignoreEvent() {
    return false;
  }
}

function citeLabel(state: EditorState, href: string, text: string) {
  const id = /^bl:\/\/cite\/([0-9a-f-]{36})/i.exec(href)?.[1]?.toLowerCase();
  const item = id ? state.facet(citeLabelsFacet)?.current?.items().find((i) => i.id.toLowerCase() === id) : null;
  if (!item) return null;
  // A page or other locator written after the key ("[@smith2024, p. 4]") stays in the label.
  const loc = /^@[^\s,]+,\s*(.+)$/.exec(text.trim())?.[1];
  return `(${item.authors || "Unknown"}, ${item.year ?? "n.d."}${loc ? `, ${loc}` : ""})`;
}

export function livePreview(openLink: { current: OpenLinkHandler | null | undefined }, labels?: CiteLabels, figures?: FigureRef): Extension {
  return [
    ...(labels ? [citeLabelsFacet.of(labels)] : []),
    ...(figures ? [figureFacet.of(figures)] : []),
    focusField,
    EditorView.focusChangeEffect.of((_state, focusing) => setFocus.of(focusing)),
    inlinePlugin,
    blockField,
    EditorView.domEventHandlers({
      mousedown(e, view) {
        const el = (e.target as HTMLElement | null)?.closest?.("[data-href]");
        if (!el || e.button !== 0 || e.altKey || e.shiftKey) return false;
        const href = el.getAttribute("data-href") ?? "";
        e.preventDefault();
        if (href.startsWith("bl://")) openLink.current?.(href, linkContext(view.state, view.posAtDOM(el)));
        else if (/^https?:\/\//i.test(href)) window.open(href, "_blank", "noopener,noreferrer");
        return true;
      },
    }),
  ];
}
